import React, { useState, useMemo } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LayoutSingleColumn, NamedLink } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import { SECTIONS, ALL_RESOURCES, CALENDAR_DATA } from './resourcesData';
import css from './ResourcesPage.module.css';
import heroImage from '../../assets/resources-hero.jpg';

const getTagClass = tag => {
  if (!tag) return undefined;
  if (tag.includes('Federal')) return css.resourceTagFederal;
  if (tag.includes('Insurance')) return css.resourceTagInsurance;
  if (tag.includes('State')) return css.resourceTagState;
  if (tag.includes('Free') || tag.includes('Sign Up')) return css.resourceTagFree;
  return undefined;
};

const matchesQuery = (item, q) =>
  `${item.title} ${item.description} ${item.tag || ''} ${item.sectionChip || ''}`
    .toLowerCase()
    .includes(q);

// Resource card. When `categoryLabel` is set (filtered view) it shows the section
// it came from; otherwise it shows the item's own tag (grouped view).
const ResourceCard = ({ item, categoryLabel }) => (
  <a href={item.url} target="_blank" rel="noopener noreferrer" className={css.resourceCard}>
    <div className={css.cardTopRow}>
      {item.icon ? <span className={css.resourceIcon}>{item.icon}</span> : null}
      {categoryLabel ? (
        <span className={css.resourceCategory}>{categoryLabel}</span>
      ) : item.tag ? (
        <span className={classNames(css.resourceTag, getTagClass(item.tag))}>{item.tag}</span>
      ) : null}
    </div>
    <h3 className={css.resourceTitle}>{item.title}</h3>
    <p className={css.resourceDescription}>{item.description}</p>
    <span className={css.resourceLink}>Visit site →</span>
  </a>
);

const SeasonCard = ({ season }) => (
  <div className={classNames(css.seasonCard, css[`season_${season.color}`])}>
    <div className={css.seasonHeader}>
      <span className={css.seasonIcon}>{season.icon}</span>
      <div>
        <div className={css.seasonName}>{season.season}</div>
        <div className={css.seasonMonths}>{season.months}</div>
      </div>
    </div>
    <div className={css.seasonTitle}>{season.title}</div>
    <ul className={css.seasonTasks}>
      {season.tasks.map((t, i) => (
        <li key={i}>{t}</li>
      ))}
    </ul>
  </div>
);

export const ResourcesPageComponent = props => {
  const { scrollingDisabled } = props;
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');

  const q = query.trim().toLowerCase();
  const isFiltering = q.length > 0 || category !== 'all';

  // Items matching the search query (across all categories).
  const queryMatched = useMemo(() => (q ? ALL_RESOURCES.filter(i => matchesQuery(i, q)) : ALL_RESOURCES), [q]);

  // Counts per category for the chips, reflecting the current query.
  const countBySection = useMemo(() => {
    const m = {};
    queryMatched.forEach(i => {
      m[i.sectionId] = (m[i.sectionId] || 0) + 1;
    });
    return m;
  }, [queryMatched]);

  // Final list shown in filtered view.
  const filtered = category === 'all' ? queryMatched : queryMatched.filter(i => i.sectionId === category);

  const chips = [{ id: 'all', label: 'All', count: queryMatched.length }].concat(
    SECTIONS.map(s => ({ id: s.id, label: s.chip, count: countBySection[s.id] || 0 }))
  );

  return (
    <Page
      title="Wildfire Resources & Links | CA Fire Defense"
      description="Grants, FAIR Plan insurance discounts, free chipping programs, and California wildfire compliance resources — curated for Sierra foothills homeowners."
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          {/* Hero */}
          <div className={css.hero} style={{ backgroundImage: `url(${heroImage})` }}>
            <div className={css.heroOverlay} />
            <div className={css.heroContent}>
              <h1 className={css.heroTitle}>Wildfire Resources &amp; Links</h1>
              <p className={css.heroSubtitle}>
                Curated grants, insurance programs, local services, emergency tools, and official
                resources for California homeowners in wildfire zones.
              </p>
            </div>
          </div>

          {/* Sticky search + filter bar */}
          <div className={css.controls}>
            <div className={css.controlsInner}>
              <div className={css.searchWrap}>
                <span className={css.searchIcon} aria-hidden="true">
                  🔍
                </span>
                <input
                  type="search"
                  className={css.searchInput}
                  placeholder="Search resources — grants, insurance, defensible space…"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  aria-label="Search resources"
                />
                {query ? (
                  <button
                    type="button"
                    className={css.searchClear}
                    onClick={() => setQuery('')}
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                ) : null}
              </div>
              <div className={css.chips}>
                {chips.map(chip => (
                  <button
                    key={chip.id}
                    type="button"
                    className={classNames(css.chip, {
                      [css.chipActive]: category === chip.id,
                      [css.chipEmpty]: chip.count === 0,
                    })}
                    onClick={() => setCategory(chip.id)}
                  >
                    {chip.label}
                    <span className={css.chipCount}>{chip.count}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Content */}
          {isFiltering ? (
            <section className={css.resultsSection}>
              <div className={css.sectionInner}>
                <p className={css.resultsCount}>
                  {filtered.length} {filtered.length === 1 ? 'resource' : 'resources'}
                  {q ? <> for “{query.trim()}”</> : null}
                </p>
                {filtered.length > 0 ? (
                  <div className={css.resourceGrid}>
                    {filtered.map((item, i) => (
                      <ResourceCard key={`${item.url}-${i}`} item={item} categoryLabel={item.sectionChip} />
                    ))}
                  </div>
                ) : (
                  <div className={css.empty}>
                    <div className={css.emptyIcon}>🔍</div>
                    <p className={css.emptyText}>No resources match your search.</p>
                    <button
                      type="button"
                      className={css.emptyReset}
                      onClick={() => {
                        setQuery('');
                        setCategory('all');
                      }}
                    >
                      Clear filters
                    </button>
                  </div>
                )}
              </div>
            </section>
          ) : (
            <>
              {SECTIONS.map((section, idx) => (
                <section
                  key={section.id}
                  id={section.id}
                  className={idx % 2 === 0 ? css.sectionLight : css.sectionWhite}
                >
                  <div className={css.sectionInner}>
                    <span className={css.sectionEyebrow}>{section.eyebrow}</span>
                    <h2 className={css.sectionTitle}>{section.title}</h2>
                    <p className={css.sectionSubtitle}>{section.subtitle}</p>
                    <div className={css.resourceGrid}>
                      {section.data.map((item, i) => (
                        <ResourceCard key={i} item={item} />
                      ))}
                    </div>
                  </div>
                </section>
              ))}

              {/* Seasonal fire-safety calendar */}
              <section className={css.sectionWhite}>
                <div className={css.sectionInner}>
                  <span className={css.sectionEyebrow}>Year-Round</span>
                  <h2 className={css.sectionTitle}>Seasonal Fire-Safety Calendar</h2>
                  <p className={css.sectionSubtitle}>
                    A simple rhythm of what to do each season to stay ahead of fire season.
                  </p>
                  <div className={css.seasonGrid}>
                    {CALENDAR_DATA.map(season => (
                      <SeasonCard key={season.season} season={season} />
                    ))}
                  </div>
                </div>
              </section>
            </>
          )}

          {/* Bottom CTA */}
          <div className={css.bottomCta}>
            <div className={css.bottomCtaInner}>
              <h2 className={css.bottomCtaTitle}>Not Sure Where to Start?</h2>
              <p className={css.bottomCtaText}>
                Describe your property and we'll connect you with local professionals who
                can help.
              </p>
              <NamedLink
                name="RequestIntakePage"
                params={{ category: 'consultation' }}
                className={css.bottomCtaButton}
              >
                Get a Free Consultation
              </NamedLink>
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const ResourcesPage = compose(connect(mapStateToProps))(ResourcesPageComponent);

export default ResourcesPage;
